import { removeFieldErrors, setFieldError } from '../utils/utils';
import { Entity, EntityIdentifier, EntityIdentifierOptionsType } from './register-form/entity-identifier';

export function initRegisterForm() : void {
    const $registerForm = document.querySelector<HTMLFormElement>('#register-form');

    if ($registerForm) {
        new RegisterForm($registerForm, {
            validateFieldsOnSubmit: true,
            allowPrefilledEntityModifications: true,
            entityIdentifierOptions: {
                allowExistingThirdparties: false,
            },
        });
    }
}

type RegisterFormOptionsType = {
    validateFieldsOnSubmit: boolean, // Relance ou non les vérifications lors de la soumission du formulaire
    allowPrefilledEntityModifications: boolean, // Autorise ou non la modification des champs pré-remplis par l'API
    entityIdentifierOptions: EntityIdentifierOptionsType,
};

export class RegisterForm {
    public readonly $form: HTMLElement;

    private readonly options: RegisterFormOptionsType;

    private readonly $stepTwoSection: HTMLElement;
    private readonly $acceptanceInput: HTMLInputElement;
    private readonly $acceptanceInputError: HTMLElement;

    // Entity identifier elements
    public readonly entityIdentifier: EntityIdentifier;

    // Entity informations elements
    private readonly $entityAlreadyExists: HTMLInputElement|null;
    private readonly $entityNameInput: HTMLInputElement;
    private readonly $entityNameInputError: HTMLElement;
    private readonly $entityAddressInput: HTMLInputElement;
    private readonly $entityAddressInputError: HTMLElement;
    private readonly $entityZipCodeInput: HTMLInputElement;
    private readonly $entityZipCodeInputError: HTMLElement;
    private readonly $entityCityInput: HTMLInputElement;
    private readonly $entityCityInputError: HTMLElement;
    private readonly $entityCountrySelect: HTMLSelectElement;
    private readonly $entityCountrySelectError: HTMLElement;
    private readonly $entityPhoneCodeSelect: HTMLSelectElement;
    private readonly $entityPhoneInput: HTMLInputElement;
    private readonly $entityPhoneInputError: HTMLElement;
    private readonly $entityNafInput: HTMLInputElement;

    // Contact informations elements
    private readonly $contactGenderRadios: NodeListOf<HTMLInputElement>;
    private readonly $contactGenderRadiosError: HTMLElement;
    private readonly $contactFirstnameInput: HTMLInputElement;
    private readonly $contactFirstnameInputError: HTMLElement;
    private readonly $contactLastnameInput: HTMLInputElement;
    private readonly $contactLastnameInputError: HTMLElement;
    private readonly $contactEmailInput: HTMLInputElement;
    private readonly $contactConfirmEmailInput: HTMLInputElement;
    private readonly $contactEmailInputError: HTMLElement;
    private readonly $contactConfirmEmailInputError: HTMLElement;
    private readonly $contactPhoneInput: HTMLInputElement;
    private readonly $contactPhoneInputError: HTMLElement;

    constructor($container: HTMLElement, options: RegisterFormOptionsType) {
        this.$form = $container;
        this.options = options;
        this.$stepTwoSection = this.$form.querySelector('[register-form-step-two]') as HTMLElement;
        this.$acceptanceInput = this.$form.querySelector('.form-acceptance') as HTMLInputElement;
        this.$acceptanceInputError = this.$form.querySelector('.form-error[class$=acceptance-error]') as HTMLElement;

        // Get all entity identifier elements
        this.entityIdentifier = new EntityIdentifier(
            options.entityIdentifierOptions,
            this.$form.querySelectorAll('.form-entity-identifier-type'),
            this.$form.querySelector('.bloc-entity-identifier-search') as HTMLElement,
            this.$form.querySelector('.bloc-entity-identifier-error') as HTMLElement,
            this.$form.querySelector('.form-entity-identifier-label') as HTMLLabelElement,
            this.$form.querySelector('.form-entity-identifier') as HTMLInputElement,
            this.$form.querySelector('.form-entity-identifier-help') as HTMLElement,
            this.$form.querySelector('.form-error[class$=entity-identifier-error]') as HTMLElement,
            this.$form.querySelector('.form-entity-identifier-search') as HTMLButtonElement,
            this.$form.querySelector('.form-entity-identifier-edit') as HTMLButtonElement,
        );

        // Get all entity informations elements
        this.$entityAlreadyExists = this.$form.querySelector<HTMLInputElement>('.form-entity-already-exists');
        this.$entityNameInput = this.$form.querySelector('.form-entity-name') as HTMLInputElement;
        this.$entityNameInputError = this.$form.querySelector('.form-error[class$=entity-name-error]') as HTMLElement;
        this.$entityAddressInput = this.$form.querySelector('.form-entity-address') as HTMLInputElement;
        this.$entityAddressInputError = this.$form.querySelector('.form-error[class$=entity-address-error]') as HTMLElement;
        this.$entityZipCodeInput = this.$form.querySelector('.form-entity-zipcode') as HTMLInputElement;
        this.$entityZipCodeInputError = this.$form.querySelector('.form-error[class$=entity-zipcode-error]') as HTMLElement;
        this.$entityCityInput = this.$form.querySelector('.form-entity-city') as HTMLInputElement;
        this.$entityCityInputError = this.$form.querySelector('.form-error[class$=entity-city-error]') as HTMLElement;
        this.$entityCountrySelect = this.$form.querySelector('.form-entity-country') as HTMLSelectElement;
        this.$entityCountrySelectError = this.$form.querySelector('.form-error[class$=entity-country-error]') as HTMLElement;
        this.$entityPhoneCodeSelect = this.$form.querySelector('.form-entity-internationel-tel .field-tel-select') as HTMLSelectElement;
        this.$entityPhoneInput = this.$form.querySelector('.form-entity-phone') as HTMLInputElement;
        this.$entityPhoneInputError = this.$form.querySelector('.form-error[class$=entity-phone-error]') as HTMLElement;
        this.$entityNafInput = this.$form.querySelector('.form-entity-naf') as HTMLInputElement;

        // Get all contact informations elements
        this.$contactGenderRadios = this.$form.querySelectorAll<HTMLInputElement>('.form-contact-gender');
        this.$contactGenderRadiosError = this.$form.querySelector('.form-error[class$=contact-gender-error]') as HTMLElement;
        this.$contactFirstnameInput = this.$form.querySelector('.form-contact-firstname') as HTMLInputElement;
        this.$contactFirstnameInputError = this.$form.querySelector('.form-error[class$=contact-firstname-error]') as HTMLElement;
        this.$contactLastnameInput = this.$form.querySelector('.form-contact-lastname') as HTMLInputElement;
        this.$contactLastnameInputError = this.$form.querySelector('.form-error[class$=contact-lastname-error]') as HTMLElement;
        this.$contactEmailInput = this.$form.querySelector('.form-contact-email') as HTMLInputElement;
        this.$contactConfirmEmailInput = this.$form.querySelector('.form-contact-confirm-email') as HTMLInputElement;
        this.$contactEmailInputError = this.$form.querySelector('.form-error[class$=contact-email-first-error]') as HTMLElement;
        this.$contactConfirmEmailInputError = this.$form.querySelector('.form-error[class$=contact-email-second-error]') as HTMLElement;
        this.$contactPhoneInput = this.$form.querySelector('.form-contact-phone') as HTMLInputElement;
        this.$contactPhoneInputError = this.$form.querySelector('.form-error[class$=contact-phone-error]') as HTMLElement;

        // Initialize all forms events
        this.initFormEvents();
    }

    /**
     * Init all form events
     * 
     * @return {void}
     */
    private initFormEvents() : void {
        this.changeReadOnlyFields({});

        this.entityIdentifier
            .on('entityFounded', entity => {
                this.removeEntityInformationsErrors();
                this.removeContactInformationsErrors();

                this.fillEntityInformations(entity);
                this.showStepTwoSection();
            })
            .on('reset', () => {
                this.removeEntityInformationsErrors();
                this.removeContactInformationsErrors();

                this.fillEntityInformations({});
                this.hideStepTwoSection();
            });

        // Change l'indicatif téléphonique lorsque l'utilisateur change le pays de son entreprise
        // et qu'il n'a pas déjà renseigner un numéro de téléphone
        this.$entityCountrySelect.addEventListener('change', () => {
            if ('' === this.$entityPhoneInput.value.trim()) {
                const $option = this.$entityPhoneCodeSelect.querySelector<HTMLOptionElement>(`[data-country-code="${this.$entityCountrySelect.value}"]`);

                if ($option) {
                    this.$entityPhoneCodeSelect.value = $option.value;
                    this.$entityPhoneCodeSelect.dispatchEvent(new Event('change'));
                }
            }
        });

        if (this.options.validateFieldsOnSubmit) {
            this.$form.addEventListener('submit', (e) => {
                this.removeEntityInformationsErrors();
                this.removeContactInformationsErrors();

                if (!this.validateEntityInformations()) {
                    e.preventDefault();
                }

                if (!this.validateContactInformations()) {
                    e.preventDefault();
                }
                
                if (!this.validateFormRest()) {
                    e.preventDefault();
                }
            });
        }
    }

    /**
     * Rempli les champs concernant l'entité
     * 
     * @param {Entity} entity
     * 
     * @return {void}
     */
    private fillEntityInformations(entity: Entity): void {
        this.$entityNameInput.value = entity.name ?? '';
        this.$entityAddressInput.value = entity.address ?? '';
        this.$entityCityInput.value = entity.city ?? '';
        this.$entityZipCodeInput.value = entity.zipCode ?? '';

        this.$entityCountrySelect.value = entity.countryCode ?? this.$entityCountrySelect.options[0].value;
        this.$entityCountrySelect.dispatchEvent(new Event('change'));

        this.$entityPhoneInput.value = entity.phoneNumber ?? '';
        this.$entityNafInput.value = entity.naf ?? '';

        if (entity.phoneCode) {
            if (Number.isInteger(entity.phoneCode)) {
                this.$entityPhoneCodeSelect.value = this.$entityPhoneCodeSelect.querySelector<HTMLOptionElement>(`option[data-code="${entity.phoneCode}"]`)?.value ?? this.$entityPhoneCodeSelect.options[0].value;
            } else {
                this.$entityPhoneCodeSelect.value = entity.phoneCode as string ?? this.$entityPhoneCodeSelect.options[0].value;
            }
        }

        if (this.$entityAlreadyExists) {
            this.$entityAlreadyExists.value = entity.refExt ? '1' : '0';
        }

        this.changeReadOnlyFields(entity);
    }

    /**
     * Ajoute/Retire l'attribut `readonly` sur les champs de l'entité en fonction des options de la classe
     * 
     * @param {Entity} entity
     * 
     * @return {void}
     */
    private changeReadOnlyFields(entity: Entity): void {
        // Le champ contenant le nom de l'entreprise n'est pas modifiable
        this.$entityNameInput.toggleAttribute('readonly', !!this.$entityNameInput.value);

        // Les autres champs ne sont pas modifiables si l'option `allowPrefilledEntityModifications` est à `false`
        if (!this.options.allowPrefilledEntityModifications) {
            this.$entityAddressInput.toggleAttribute('readonly', !!this.$entityAddressInput.value);
            this.$entityCityInput.toggleAttribute('readonly', !!this.$entityCityInput.value);
            this.$entityZipCodeInput.toggleAttribute('readonly', !!this.$entityZipCodeInput.value);
            this.$entityCountrySelect.toggleAttribute('readonly', !!entity?.countryCode);
            this.$entityPhoneInput.toggleAttribute('readonly', !!this.$entityPhoneInput.value);
            this.$entityNafInput.toggleAttribute('readonly', !!this.$entityNafInput.value);
            this.$entityPhoneCodeSelect.toggleAttribute('readonly', !!this.$entityPhoneInput.value);
            this.$entityPhoneInput.toggleAttribute('readonly', !!this.$entityPhoneInput.value);
        }
    }

    /**
     * Supprime les erreurs des champs concernant l'entité
     * 
     * @return {void}
     */
    private removeEntityInformationsErrors(): void {
        const $entityInformationsContainer = this.$form.querySelector<HTMLElement>('.register-form-entity-fields');
        if ($entityInformationsContainer) {
            removeFieldErrors($entityInformationsContainer);
        }
    }

    /**
     * Supprime les erreurs des champs concernant le contact
     * 
     * @return {void}
     */
    private removeContactInformationsErrors(): void {
        const $contactInformationsContainer = this.$form.querySelector<HTMLElement>('.register-form-contact-fields');
        if ($contactInformationsContainer) {
            removeFieldErrors($contactInformationsContainer);
        }
    }

    /**
     * Show the step two section
     * 
     * @return {void}
     */
    private showStepTwoSection() : void {
        this.$stepTwoSection.classList.remove('hidden');
    }

    /**
     * Hide the step two section
     * 
     * @return {void}
     */
    private hideStepTwoSection() : void {
        this.$stepTwoSection.classList.add('hidden');
    }

    /**
     * Validate the value of entity informations inputs
     * 
     * @param {string[]} fieldsIdToValidate Only validate these fields (keep empty to validate all fields)
     * 
     * @return {boolean}
     */
    private validateEntityInformations(fieldsIdToValidate: string[] = []) : boolean {
        type FieldsToVerifyType = {
            type?: string,
            fieldId: string,
            $field: HTMLInputElement|HTMLSelectElement,
            $errorContainer: HTMLElement,
            error: boolean,
            validChars?: boolean,
            validCharsEntityName?: boolean,
        }[];

        const fieldsToVerify: FieldsToVerifyType = [
            {
                fieldId: 'entity_name',
                $field: this.$entityNameInput,
                $errorContainer: this.$entityNameInputError,
                error: false,
                validCharsEntityName: true,
            },
            {
                fieldId: 'entity_address',
                $field: this.$entityAddressInput,
                $errorContainer: this.$entityAddressInputError,
                error: false,
                validChars: true,
            },
            {
                fieldId: 'entity_zipcode',
                $field: this.$entityZipCodeInput,
                $errorContainer: this.$entityZipCodeInputError,
                error: false,
                validChars: true,
            },
            {
                fieldId: 'entity_city',
                $field: this.$entityCityInput,
                $errorContainer: this.$entityCityInputError,
                error: false,
                validChars: true,
            },
            {
                fieldId: 'entity_country',
                $field: this.$entityCountrySelect,
                $errorContainer: this.$entityCountrySelectError,
                error: false,
            },
            {
                type: 'phone',
                fieldId: 'entity_phone',
                $field: this.$entityPhoneInput,
                $errorContainer: this.$entityPhoneInputError,
                error: false,
            },
        ].filter(fieldAttr => {
            if (fieldsIdToValidate.length === 0) {
                return fieldAttr;
            }

            return fieldsIdToValidate.includes(fieldAttr.fieldId);
        });

        fieldsToVerify.forEach(attr => {
            if ('canBeEmpty' !== attr.type) {
                const errorMessage = attr.$field.dataset.errorMessage ?? '';
                const value = attr.$field.value;

                if ('' === value) {
                    attr.error = true;
                    setFieldError(attr.$errorContainer, errorMessage);
                } else {
                    attr.error = false;
                    removeFieldErrors(attr.$errorContainer);
                }
            }

            if (!attr.error) {
                if (
                    (true === attr.validChars && /[$<>&~"#{}[\]()|`\\/+=^%*?;:!]+/.test(attr.$field.value)) ||
                    (true === attr.validCharsEntityName && /[$<>~"#{}[\]()|`\\+=^%*?;:!]/.test(attr.$field.value))
                ) {
                    const errorMessage = attr.$field.dataset.invalidCharsMessage ?? '';

                    attr.error = true;
                    setFieldError(attr.$errorContainer, errorMessage);
                }
            }
        });

        const hasError = fieldsToVerify.filter(attr => true === attr.error).length > 0;

        return !hasError;
    }

    /**
     * Validate the value of contact informations inputs
     * 
     * @return {boolean}
     */
    private validateContactInformations() : boolean {
        type FieldsToVerifyType = {
            type?: string,
            $field: HTMLInputElement|NodeListOf<HTMLInputElement>,
            $confirmField?: HTMLInputElement,
            $confirmErrorContainer?: HTMLElement,
            $errorContainer: HTMLElement,
            error: boolean,
            validChars?: boolean,
        }[];

        const fieldsToVerify: FieldsToVerifyType = [
            {
                type: 'radio',
                $field: this.$contactGenderRadios,
                $errorContainer: this.$contactGenderRadiosError,
                error: false,
            },
            {
                $field: this.$contactFirstnameInput,
                $errorContainer: this.$contactFirstnameInputError,
                error: false,
                validChars: true,
            },
            {
                $field: this.$contactLastnameInput,
                $errorContainer: this.$contactLastnameInputError,
                error: false,
                validChars: true,
            },
            {
                type: 'email',
                $confirmField: this.$contactConfirmEmailInput,
                $confirmErrorContainer: this.$contactConfirmEmailInputError,
                $field: this.$contactEmailInput,
                $errorContainer: this.$contactEmailInputError,
                error: false,
            },
            {
                type: 'phone',
                $field: this.$contactPhoneInput,
                $errorContainer: this.$contactPhoneInputError,
                error: false,
            },
        ];
        
        fieldsToVerify.forEach(attr => {
            if (!attr.$field) {
                return;
            }

            if ('radio' === attr.type && attr.$field instanceof NodeList) {
                const $checked = Array.from(attr.$field).filter($f => $f.checked);
                const errorMessage = attr.$field[0].dataset.errorMessage ?? '';

                if (0 === $checked.length) {
                    attr.error = true;
                    setFieldError(attr.$errorContainer, errorMessage);
                } else {
                    attr.error = false;
                    removeFieldErrors(attr.$errorContainer);
                }
            } else if ('email' === attr.type && attr.$field instanceof HTMLInputElement) {
                const regex = /[^@ \t\r\n]+@[^@ \t\r\n]+.[^@ \t\r\n]+/;

                if (!regex.test(attr.$field.value)) {
                    const errorMessage = attr.$field.dataset.errorMessage ?? '';

                    attr.error = true;
                    setFieldError(attr.$errorContainer, errorMessage);
                    setFieldError((attr.$confirmErrorContainer as HTMLElement), '&nbsp;');
                    if (attr.$confirmField) {
                        setFieldError(attr.$confirmField, '');
                    }
                } else if (attr.$confirmField && attr.$field.value !== attr.$confirmField.value) {
                    const errorMessage = attr.$field.dataset.notMatchMessage ?? '';

                    attr.error = true;
                    setFieldError(attr.$errorContainer, errorMessage);
                    setFieldError((attr.$confirmErrorContainer as HTMLElement), '&nbsp;');
                    if (attr.$confirmField) {
                        setFieldError(attr.$confirmField, '');
                    }
                } else {
                    attr.error = false;
                    removeFieldErrors(attr.$errorContainer);
                    removeFieldErrors(attr.$confirmField as HTMLInputElement);
                    if (attr.$confirmField) {
                        removeFieldErrors(attr.$confirmField);
                    }
                }
            } else if (attr.$field instanceof HTMLInputElement) {
                const errorMessage = attr.$field.dataset.errorMessage ?? '';
                const value = attr.$field.value;

                if ('' === value) {
                    attr.error = true;
                    setFieldError(attr.$errorContainer, errorMessage);
                } else {
                    attr.error = false;
                    removeFieldErrors(attr.$errorContainer);
                }
            }

            if (!attr.error && true === attr.validChars && attr.$field instanceof HTMLInputElement) {
                if (/[$<>&~"#{}[\]()|`\\/+=^%*?;:!]+/.test(attr.$field.value)) {
                    const errorMessage = attr.$field.dataset.invalidCharsMessage ?? '';

                    attr.error = true;
                    setFieldError(attr.$errorContainer, errorMessage);
                }
            }
        });

        const hasError = fieldsToVerify.filter(attr => true === attr.error).length > 0;

        return !hasError;
    }

    /**
     * Validate the value of form rest
     * 
     * @return {boolean}
     */
    private validateFormRest() : boolean {
        const fieldsToVerify = [
            {
                type: 'checkbox',
                $field: this.$acceptanceInput,
                $errorContainer: this.$acceptanceInputError,
                error: false,
            },
        ];
        
        fieldsToVerify.forEach(attr => {
            if (attr.$field) {
                if ('checkbox' === attr.type) {
                    if (!attr.$field.checked) {
                        const errorMessage = attr.$field.dataset.errorMessage ?? '';

                        attr.error = true;
                        setFieldError(attr.$errorContainer, errorMessage);
                    } else {
                        attr.error = false;
                        removeFieldErrors(attr.$errorContainer);
                    }
                }
            }
        });

        const hasError = fieldsToVerify.filter(attr => true === attr.error).length > 0;

        return !hasError;
    }
}
